import { useLayoutEffect } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import * as S from './MyAdsBulkPromotion.styles';
import { MyAdsBulkPromotionFlow } from './MyAdsBulkPromotionFlow';
import {
  CardWrapper,
  Grid,
} from '@/features/Profile/sections/MyAds/components/Ads/Ads.styles';
import { AdsEmpty } from '@/features/Profile/sections/MyAds/components/AdsEmpty/AdsEmpty';
import {
  useMyAdsBulkPromotion,
  useMyAdsBulkPromotionPayment,
} from '@/features/Profile/sections/MyAds/hooks';
import { AdCardNew } from '@/components/AdCardNew';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { Button } from '@/components/UI/Button/Button';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { BULK_PROMOTION_MAX_SELECTION } from '@/features/Profile/sections/MyAds/MyAdsSection.constants';
import type { PaymentResult } from '@/api/types/payments.types';
import { WarningModal } from '@/components/Ad/WarningModal';
import { useResponsive } from '@/hooks/useResponsive';

const InfiniteLoader = dynamic(
  () =>
    import('@/components/InfiniteLoader/InfiniteLoader').then(
      module => module.InfiniteLoader
    ),
  { ssr: false }
);

type Props = {
  onClose: () => void;
  onPaymentSuccess: (payment: PaymentResult) => void;
};

export function MyAdsBulkPromotion({ onClose, onPaymentSuccess }: Props) {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const {
    ads,
    canContinue,
    canLoadMore,
    closeCancelConfirmation,
    confirmCancellation,
    handleLoadMore,
    isCancelConfirmationOpen,
    isLoadingMore,
    isSelected,
    loading,
    requestCancellation,
    selectedCount,
    selectedIds,
    toggle,
  } = useMyAdsBulkPromotion({ onClose });

  const payment = useMyAdsBulkPromotionPayment({
    targetIds: selectedIds,
    onPaymentSuccess,
  });

  const continueLabel = `${t('common.continue')} (${selectedCount})`;

  useLayoutEffect(() => {
    if (isDesktop) return;

    window.scrollTo(0, 0);

    return () => window.scrollTo(0, 0);
  }, [isDesktop]);

  function renderLoadingCards(count: AdsChunkSize) {
    return Array.from({ length: count }, (_, index) => (
      <CardWrapper key={`loading-${index}`}>
        <AdCardNew />
      </CardWrapper>
    ));
  }

  return (
    <>
      <S.Header>
        <S.HeaderLeft>
          <S.BackButton
            data-stat="bulk-back"
            aria-label={t('common.advertise')}
            onClick={onClose}
          >
            <AngleBackIcon />
          </S.BackButton>
          <S.Title $variant="2">{t('common.advertise')}</S.Title>
        </S.HeaderLeft>
        <S.CancelButton data-stat="bulk-cancel" onClick={requestCancellation}>
          {t('common.decline')}
        </S.CancelButton>
      </S.Header>

      <S.SelectionContent>
        <S.Banner>
          <span>{t('pages.cabinet.bulk_promotion_select_hint')}</span>
          <span>
            {t('pages.cabinet.bulk_promotion_limit_hint', {
              count: BULK_PROMOTION_MAX_SELECTION,
            })}
          </span>
        </S.Banner>

        {loading && (
          <Grid data-testid={CommonTestIds.adCards.cardsBox}>
            {renderLoadingCards(AdsChunkSize.Middle)}
          </Grid>
        )}

        {!loading && ads.length > 0 && (
          <Grid data-testid={CommonTestIds.adCards.cardsBox}>
            {ads.map(ad => (
              <CardWrapper key={ad.id}>
                <AdCardNew
                  ad={ad}
                  hasBookmarkButton={false}
                  hasStatusOverlay={false}
                  selection={{
                    isActive: true,
                    isSelected: isSelected(ad.id),
                    onToggle: () => toggle(ad.id),
                  }}
                />
              </CardWrapper>
            ))}
            {isLoadingMore && renderLoadingCards(AdsChunkSize.Small)}
          </Grid>
        )}

        {!loading && ads.length === 0 && <AdsEmpty />}

        {!loading && ads.length > 0 && canLoadMore && (
          <InfiniteLoader onVisit={handleLoadMore} rootMargin={200} />
        )}
      </S.SelectionContent>

      {canContinue && (
        <S.Footer>
          <S.ContinueButtonBox>
            <Button
              data-stat="bulk-continue"
              appearance="secondary"
              isFullWidth
              height={54}
              borderRadius={14}
              onClick={payment.openServicePicker}
            >
              {continueLabel}
            </Button>
          </S.ContinueButtonBox>
        </S.Footer>
      )}

      <MyAdsBulkPromotionFlow selectedCount={selectedCount} payment={payment} />

      <WarningModal
        isOpen={isCancelConfirmationOpen}
        onClose={closeCancelConfirmation}
        title={t('pages.cabinet.bulk_promotion_cancel_title')}
        description={t('pages.cabinet.bulk_promotion_cancel_description')}
        actions={
          <>
            <Button
              appearance="secondaryOutlined"
              isFullWidth
              borderRadius={14}
              onClick={closeCancelConfirmation}
            >
              {t('common.close')}
            </Button>
            <Button
              appearance="secondary"
              isFullWidth
              borderRadius={14}
              onClick={confirmCancellation}
            >
              {t('pages.cabinet.bulk_promotion_cancel_action')}
            </Button>
          </>
        }
      />
    </>
  );
}
